import { GetServerSideProps } from 'next';
import { Cookies, ListResponseData, MenuResponse, ProductComment, ProductInstance, ProductRelated } from 'types';
import {
  getGearInstance,
  getMenuData,
  getGearRelated,
  getGearComments,
  getGearLike,
  getGearLikeSizes,
  getGearSimilarBrands,
  getTopSellers,
  getExternalReviews,
  getPRPosts,
} from 'api';
import { getCommonOgData, getPageDescription, getProductPageLdJson } from 'utils/seoFormatters';
import { getCanonicalUrlFromContext } from 'utils/formatters';
import { ProductPage, ProductPageProps } from 'containers/ProductPage';
import { ErrorPage } from 'containers/ErrorPage';
import { useRouter } from 'next/router';

const GearPage = (props: ProductPageProps) => {
  const router = useRouter();
  const { product } = props;
  if (!Object.keys(product || {}).length) {
    return (
      <ErrorPage
        {...props}
        statusCode="404"
        title="Oops"
        errorMessage="This product might have already sold. Gear moves fast here."
        primaryButton={{
          action: () => router.push('/'),
          caption: 'Keep Searching',
        }}
      />
    );
  }

  return <ProductPage {...props} />;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const slug = String(context.query.slug);
  const force = !!context.query.force;
  // const withLike = context.query.withLike === '1';
  const cookies: Cookies = {
    parsed: context.req.cookies as Record<string, string>,
  };

  const menuDataRequest: Promise<MenuResponse> = getMenuData(force);
  const productRequest: Promise<ProductInstance> = getGearInstance(slug, cookies);
  const relatedRequest: Promise<ProductRelated> = getGearRelated(slug);
  const commentsRequest: Promise<ListResponseData<ProductComment[]>> = getGearComments(slug);

  const requests = [
    menuDataRequest,
    productRequest,
    relatedRequest,
    commentsRequest,
  ];

  // if (withLike) {
    requests.push(getGearLike(slug, { limit: 12 }));
    requests.push(getGearLikeSizes(slug));
    requests.push(getGearSimilarBrands(slug));
    requests.push(getTopSellers({ limit: 8 }));
    requests.push(getTopSellers({ ambassadors: 1, limit: 8 }));
    requests.push(getExternalReviews());
    requests.push(getPRPosts());
  // }

  const [
    menuData,
    product,
    related,
    comments,
    likeGears = null,
    likeSizes = null,
    similarBrands = null,
    topSellers = null,
    topAmbassadors = null,
    externalReviews = null,
    prPosts = null,
  ] = await Promise.all(requests);

  return {
    props: {
      menuData,
      product,
      related,
      comments: comments || {},
      likeGears,
      likeSizes,
      similarBrands,
      topSellers,
      topAmbassadors,
      externalReviews,
      prPosts,
      slug,
      ldJsonData: getProductPageLdJson(product),
      ogData: getCommonOgData({
        url: getCanonicalUrlFromContext(context),
        title: product.category ? `${product.title} - ${product.category.title} | SENDY` : `${product.title} | SENDY`,
        description: getPageDescription('gear', { description: product.description }),
        banner: product.media?.images?.[0],
      }),
    },
  };
};

export default GearPage;
